// Part of @roc/feature-track-record-v2 — isolated copy of the redesigned Track
// Record UI used only by crm-portal and roc-internal-portal. The original lives
// in @roc/feature-borrowers and is left untouched so other portals keep the old
// design. MobX stores/services remain shared (imported from @roc/feature-borrowers).

import {
  Box,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles, Theme, alpha } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import PriorityHighIcon from '@material-ui/icons/PriorityHigh';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';
import { Button, FileUpload, FileUploadArea, Tabs } from '@roc/ui';
import { TrackRecordTrace, TrackRecordAgentResponse } from '@roc/feature-credit-background-check';
import { ReactComponent as AIPoweredIcon } from '../assets/AI-Powered.svg';
import { insertIf } from '@roc/feature-utils';
import { useGeneralTasksStore } from '@roc/feature-general-tasks';

const useStyles = makeStyles((theme: Theme) => ({
  dialogPaper: {
    borderRadius: 8,
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 24px 0',
  },
  tabsWrapper: {
    padding: '0 24px',
    borderBottom: '1px solid #e0e0e0',
  },
  borrowerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f5f5f5',
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    padding: '10px 16px',
    margin: '16px 0 0',
    cursor: 'default',
  },
  // --- Progress view ---
  aiIconRing: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    border: `2px solid ${theme.palette.primary.main}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  progressRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 6,
  },
  progressBar: {
    flex: 1,
    height: 6,
    borderRadius: 3,
  },
  stepsCard: {
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    padding: '8px 20px',
    marginTop: 16,
  },
  stepRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 0',
  },
  activeCircleOuter: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    border: `2px solid ${theme.palette.primary.main}`,
    backgroundColor: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
    flexShrink: 0,
  },
  activeCircleInner: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
  },
  pendingCircle: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    border: '2px solid #ccc',
    marginRight: 12,
    flexShrink: 0,
  },
  cancelButton: {
    fontSize: 12,
    padding: '3px 10px',
    minWidth: 'unset',
    letterSpacing: 0.5,
  },
  // --- Error view ---
  errorIconRing: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    backgroundColor: '#FDECEA',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tryAgainRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 12,
  },
  altCard: {
    border: '1px solid #e0e0e0',
    borderRadius: 12,
    padding: '20px 16px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: '#fff',
    height: '100%',
    transition: 'box-shadow 0.2s',
    '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.12)' },
  },
  altCardIconWrapper: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    backgroundColor: '#EEEEEE',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  dbBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: 20,
    padding: '4px 12px',
    marginTop: 10,
    color: theme.palette.primary.main,
    fontSize: 12,
    gap: 4,
  },
  // --- Success view ---
  successBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    backgroundColor: '#F1F8E9',
    border: '1px solid #C5E1A5',
    borderRadius: 6,
    padding: '14px 16px',
    marginTop: 16,
    gap: 12,
  },
  statCard: {
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#fff',
  },
  entitiesHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 16px',
    borderBottom: '1px solid #e0e0e0',
  },
  entityCountBadge: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 700,
    flexShrink: 0,
  },
  entityRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 16px',
    borderBottom: '1px solid #f0f0f0',
  },
  entityRowWarning: {
    backgroundColor: '#FFF8E1',
  },
  warningBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px solid #E65100',
    borderRadius: 20,
    padding: '2px 10px',
    color: '#E65100',
    fontSize: 12,
    gap: 4,
  },
  verificationChip: {
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px solid',
    borderRadius: 20,
    padding: '2px 10px',
    fontSize: 12,
  },
  dealTypeBadge: {
    display: 'inline-flex',
    border: '1px solid #e0e0e0',
    borderRadius: 20,
    padding: '2px 8px',
    fontSize: 11,
    color: '#555',
    whiteSpace: 'nowrap' as const,
    marginBottom: 2,
  },
  tableWrapper: {
    border: '1px solid #e0e0e0',
    borderRadius: 6,
    overflow: 'hidden',
    marginTop: 16,
  },
  tableHead: {
    backgroundColor: '#fafafa',
    '& th': {
      fontWeight: 600,
      fontSize: 12,
      color: '#555',
      padding: '8px 10px',
      borderBottom: '1px solid #e0e0e0',
      whiteSpace: 'nowrap' as const,
    },
  },
  tableRow: {
    '& td': {
      padding: '8px 10px',
      fontSize: 13,
      verticalAlign: 'top',
      borderBottom: '1px solid #f0f0f0',
    },
  },
  warningText: {
    display: 'flex',
    alignItems: 'center',
    color: '#E65100',
    fontSize: 12,
    gap: 3,
    marginTop: 2,
  },
  successFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 0 0',
    borderTop: '1px solid #e0e0e0',
    marginTop: 16,
  },
}));

// Top-level component (not declared inside the dialog's render) so React
// preserves its subtree between renders instead of remounting it.
const BorrowerHeader = ({ className, borrowerName }: { className: string; borrowerName: string }) => (
  <Box className={className}>
    <Typography variant="body2" style={{ fontWeight: 600, fontSize: 14 }}>
      {borrowerName}{' - '}
      <span style={{ fontWeight: 400, color: '#666' }}>Track Record</span>
    </Typography>
    <ExpandMoreIcon style={{ color: '#666', fontSize: 20 }} />
  </Box>
);

interface TrackRecordExtractionDialogProps {
  open: boolean;
  fileName: string;
  borrowerName: string;
  hasError?: boolean;
  extractionSuccess?: boolean;
  isLoading?: boolean;
  traceSteps?: TrackRecordTrace[];
  extractionResults?: TrackRecordAgentResponse[];
  onClose: () => void;
  onCancel: () => void;
  onTryAgain?: () => void;
  onDownloadTemplate?: () => void;
  onLookUpByEntity?: () => void;
  onLookUpByProperty?: () => void;
  onSaveAndFinish?: () => void;
  onUploadAdditionalDocument?: () => void;
  onRemoveProperty?: (index: number) => void;
}

export const TrackRecordExtractionDialog = observer(({
  open,
  fileName,
  borrowerName,
  hasError = false,
  extractionSuccess = false,
  isLoading = false,
  traceSteps = [],
  extractionResults = [],
  onClose,
  onCancel,
  onTryAgain,
  onDownloadTemplate,
  onLookUpByEntity,
  onLookUpByProperty,
  onSaveAndFinish,
  onUploadAdditionalDocument,
  onRemoveProperty,
}: TrackRecordExtractionDialogProps) => {
  const classes = useStyles();
  const [selectedTab, setSelectedTab] = useState(0);
  const [retryUploads, setRetryUploads] = useState<FileUpload[]>([]);
  const { generalTasksStore } = useGeneralTasksStore();

  const toolSteps = traceSteps.filter(t => t.step === 'calling_tool');

  const derivedEntities = useMemo(() => {
    const entityMap = new Map<string, { name: string; propertiesCount: number; verifiedCount: number }>();
    extractionResults.forEach(r => {
      const name = r['Entity Name'] || 'Unknown';
      const existing = entityMap.get(name) || { name, propertiesCount: 0, verifiedCount: 0 };
      existing.propertiesCount++;
      if (r.validation?.isVerified) existing.verifiedCount++;
      entityMap.set(name, existing);
    });
    return Array.from(entityMap.values());
  }, [extractionResults]);

  const derivedStats = useMemo(() => {
    const total = extractionResults.length;
    const autoVerified = extractionResults.filter(r => r.validation?.isVerified).length;
    const pending = total - autoVerified;
    return { propertiesFound: total, autoVerified, needsReview: 0, pending };
  }, [extractionResults]);

  useEffect(() => {
    if (open) {
      setSelectedTab(0);
      setRetryUploads([]);
    }
  }, [open]);

  const handleTryAgain = () => {
    setRetryUploads([]);
    onTryAgain?.();
  };

  const getVerificationChipStyle = (status: string) => {
    const isVerified = status === 'Verified';
    return {
      borderColor: isVerified ? '#2E7D32' : '#E65100',
      color: isVerified ? '#2E7D32' : '#E65100',
    };
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ className: classes.dialogPaper }}
      maxWidth="xl"
      fullWidth
    >
      {/* Dialog header */}
      <Box className={classes.dialogTitle}>
        <Typography variant="h5" style={{ fontWeight: 700 }}>Task</Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box className={classes.tabsWrapper}>
        <Tabs
          tabs={[{ label: 'TRACK RECORD' }, ...insertIf(!generalTasksStore.globalStore.isBorrowerPortal, [{ label: 'CHAT' }])]}
          selectedTab={selectedTab}
          onTabChange={(_e, idx) => setSelectedTab(idx)}
          variant={''}
        />
      </Box>

      <DialogContent style={{ padding: '0 24px 24px', overflowY: 'auto', maxHeight: '80vh' }}>
        {selectedTab === 0 && !hasError && !extractionSuccess && (
          <>
            <BorrowerHeader className={classes.borrowerHeader} borrowerName={borrowerName} />
            <Box display="flex" flexDirection="column" alignItems="center" style={{ padding: '28px 0 20px' }}>
              <Box className={classes.aiIconRing}>
                <AIPoweredIcon style={{ width: 28, height: 28 }} />
              </Box>
              <Typography variant="h6" style={{ fontWeight: 700, marginTop: 14, fontSize: 17 }}>
                AI is Extracting Your Data
              </Typography>
            </Box>
            <Box>
              <Box display="flex" alignItems="center">
                <Typography variant="body2" style={{ fontWeight: 700, marginRight: 4 }}>
                  Uploading Document -
                </Typography>
                <Typography variant="body2" style={{ color: '#888' }}>
                  {fileName || 'track_record.xlsx'}
                </Typography>
              </Box>
              <Box className={classes.progressRow}>
                <LinearProgress variant="indeterminate" className={classes.progressBar} style={{ flex: 1 }} />
                <Button variant="outlined" onClick={onCancel} testId="cancelExtraction" className={classes.cancelButton}>
                  CANCEL
                </Button>
              </Box>
            </Box>
            <Box style={{ borderTop: '1px solid #e0e0e0', margin: '20px 0 0' }} />
            <Box className={classes.stepsCard}>
              {toolSteps.length === 0 ? (
                <Box className={classes.stepRow}>
                  <Box className={classes.activeCircleOuter}>
                    <Box className={classes.activeCircleInner} />
                  </Box>
                  <Typography variant="body2" style={{ color: '#222', fontWeight: 600, fontSize: 14 }}>
                    Connecting to AI agent...
                  </Typography>
                </Box>
              ) : (
                toolSteps.map((step, index) => {
                  const isLatest = index === toolSteps.length - 1;
                  const isCompleted = index < toolSteps.length - 1;
                  return (
                    <Box key={index} className={classes.stepRow}>
                      {isLatest ? (
                        <Box className={classes.activeCircleOuter}>
                          <Box className={classes.activeCircleInner} />
                        </Box>
                      ) : (
                        <CheckCircleIcon style={{ color: '#2E7D32', fontSize: 20, marginRight: 12, flexShrink: 0 }} />
                      )}
                      <Typography
                        variant="body2"
                        style={{
                          color: '#222',
                          fontWeight: isLatest ? 600 : 400,
                          fontSize: 14,
                        }}
                      >
                        {step.tool || 'Processing...'}
                      </Typography>
                    </Box>
                  );
                })
              )}
            </Box>
          </>
        )}

        {selectedTab === 0 && hasError && (
          <>
            <BorrowerHeader className={classes.borrowerHeader} borrowerName={borrowerName} />
            <Box display="flex" flexDirection="column" alignItems="center" style={{ padding: '28px 0 16px' }}>
              <Box className={classes.errorIconRing}>
                <PriorityHighIcon style={{ color: '#D32F2F', fontSize: 32 }} />
              </Box>
              <Typography variant="h6" style={{ fontWeight: 700, marginTop: 14 }}>
                Unable to Extract Data
              </Typography>
              <Typography variant="body2" align="center" style={{ color: '#666', marginTop: 10, maxWidth: 360, lineHeight: 1.6 }}>
                We couldn't process your document. This might be because the file format isn't
                supported or the content isn't readable.
              </Typography>
            </Box>
            <Box style={{ backgroundColor: '#f5f5f5', borderRadius: 8, padding: '16px 20px', margin: '8px 0 24px' }}>
              <Typography variant="body2" style={{ fontWeight: 700, marginBottom: 12 }}>
                Document uploaded successfully
              </Typography>
              {[
                'Upload a different file format (Excel or CSV works best)',
                'Use our template for better extraction accuracy',
                'Enter properties manually or search by entity',
              ].map((tip, i) => (
                <Box key={i} display="flex" alignItems="center" style={{ marginBottom: i < 2 ? 8 : 0 }}>
                  <Typography variant="body2" style={{ color: '#666', marginRight: 8, fontSize: 16 }}>&rarr;</Typography>
                  <Typography variant="body2" style={{ color: '#444' }}>{tip}</Typography>
                </Box>
              ))}
            </Box>
            <Box display="flex" justifyContent="center" style={{ gap: 12, marginBottom: 8 }}>
              <Button variant="outlined" onClick={handleTryAgain} testId="tryAgainExtraction">
                TRY AGAIN
              </Button>
              <Button variant="contained" color="primary" onClick={onDownloadTemplate} testId="downloadTemplate">
                DOWNLOAD TEMPLATE
              </Button>
            </Box>
          </>
        )}

        {selectedTab === 0 && extractionSuccess && (
          <>
            <BorrowerHeader className={classes.borrowerHeader} borrowerName={borrowerName} />

            {/* Success banner */}
            <Box className={classes.successBanner}>
              <CheckCircleIcon style={{ color: '#2E7D32', fontSize: 22, flexShrink: 0, marginTop: 1 }} />
              <Box>
                <Typography variant="body1" style={{ fontWeight: 700, color: '#2E7D32', fontSize: 14 }}>
                  Extraction Complete
                </Typography>
                <Typography variant="body2" style={{ color: '#444', marginTop: 2 }}>
                  Found {extractionResults.length} properties and {derivedEntities.length} entities from your document.
                </Typography>
              </Box>
            </Box>

            {/* Stats row */}
            <Grid container spacing={2} style={{ marginTop: 12 }}>
              {[
                { count: derivedStats.propertiesFound, label: 'Properties Found', color: '#222' },
                { count: derivedStats.autoVerified, label: 'Auto-Verified', color: '#222' },
                { count: derivedStats.needsReview, label: 'Needs Review', color: '#E65100' },
                { count: derivedStats.pending, label: 'Pending', color: '#222' },
              ].map(({ count, label, color }) => (
                <Grid item xs={3} key={label}>
                  <Box className={classes.statCard}>
                    <Typography variant="h5" style={{ fontWeight: 700, color, lineHeight: 1 }}>{count}</Typography>
                    <Typography variant="body2" style={{ color: '#666', fontSize: 13 }}>{label}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Identified entities */}
            <Box style={{ border: '1px solid #e0e0e0', borderRadius: 6, marginTop: 16, overflow: 'hidden' }}>
              <Box className={classes.entitiesHeader}>
                <Box className={classes.entityCountBadge}>{derivedEntities.length}</Box>
                <Typography variant="body1" style={{ fontWeight: 700, fontSize: 14 }}>Identified Entities</Typography>
              </Box>
              {derivedEntities.map((entity, i) => {
                const allVerified = entity.verifiedCount === entity.propertiesCount;
                return (
                  <Box
                    key={i}
                    className={`${classes.entityRow} ${!allVerified ? classes.entityRowWarning : ''}`}
                  >
                    <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                      {allVerified ? (
                        <CheckCircleIcon style={{ color: '#2E7D32', fontSize: 20 }} />
                      ) : (
                        <ReportProblemOutlinedIcon style={{ color: '#E65100', fontSize: 20 }} />
                      )}
                      <Typography variant="body2" style={{ fontWeight: 500, fontSize: 14 }}>{entity.name}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <Typography variant="body2" style={{ color: '#888', fontSize: 13 }}>
                        {entity.propertiesCount} {entity.propertiesCount === 1 ? 'property' : 'properties'}
                        {' '}&bull;{' '}{entity.verifiedCount} verified
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Properties table */}
            <Box className={classes.tableWrapper}>
              <Table size="small">
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell>Type of deal</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Verification Status</TableCell>
                    <TableCell>Purchase Date</TableCell>
                    <TableCell>Purchase Amount</TableCell>
                    <TableCell>Rehab Budget</TableCell>
                    <TableCell>Sale Price</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {extractionResults.map((prop, i) => {
                    const address = [prop['Property Address'], prop['City'], prop['State'], prop['Zipcode']]
                      .filter(Boolean).join(', ');
                    const isVerified = prop.validation?.isVerified;
                    const verificationStatus = isVerified ? 'Verified' : 'Not Verified';
                    return (
                      <TableRow key={i} className={classes.tableRow}>
                        <TableCell>
                          {prop['Type of Deal'] && (
                            <Box className={classes.dealTypeBadge}>{prop['Type of Deal']}</Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" style={{ fontSize: 13 }}>{address}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box className={classes.verificationChip} style={getVerificationChipStyle(verificationStatus)}>
                            {verificationStatus}
                          </Box>
                        </TableCell>
                        <TableCell>{prop['Purchase Date'] || ''}</TableCell>
                        <TableCell>{prop['Purchase Price'] || ''}</TableCell>
                        <TableCell>{prop['Renovation Budget'] || ''}</TableCell>
                        <TableCell>{prop['Sale Price'] || ''}</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => onRemoveProperty?.(i)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            {/* Footer */}
            <Box className={classes.successFooter}>
              <Button
                variant="outlined"
                onClick={onUploadAdditionalDocument}
                testId="uploadAdditionalDocument"
              >
                UPLOAD ADDITIONAL DOCUMENT
              </Button>
              <Box display="flex" style={{ gap: 12 }}>
                <Button variant="outlined" onClick={onCancel} testId="cancelSuccess">
                  CANCEL
                </Button>
                <Button variant="contained" color="primary" onClick={onSaveAndFinish} testId="saveAndFinish">
                  SAVE AND FINISH
                </Button>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
});
